import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { useIsMounted } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMounted'
import {
  PROVIDER_TAB_SUBTITLE_MSG,
  PROVIDER_TAB_TITLE_MSG,
  TALK_WITH_PROVIDER_TAB,
  WellnessCheckInDomain,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import {
  WellbeingResponseResults,
  WellnessCheckInResultsDomainProps,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import {
  getDomainDisplayName,
  isInvalidDomain,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'

import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Subhead } from '../../atoms/subhead/Subhead'
import { WellnessCheckInResultsDomainCardScore } from '../../molecules/wellnessCheckInResultsDomainCardScore/WellnessCheckInResultsDomainCardScore'
import { WellnessCheckInResultsDomainCardScorePreview } from '../../molecules/wellnessCheckInResultsDomainCardScorePreview/WellnessCheckInResultsDomainCardScorePreview'
import { WellnessCheckInResultsDomainContent } from '../../molecules/wellnessCheckInResultsDomainContent/WellnessCheckInResultsDomainContent'
import { WellnessCheckInResultsProviderTabContent } from '../../molecules/wellnessCheckInResultsProviderTabContent/WellnessCheckInResultsProviderTabContent'
import { SubheadSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { getWellnessCheckInEssentialsContents } from '../../utils/wellnessCheckInResults/utils'

const DomainContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  margin: `${theme.spacing['24px']} 0 ${theme.spacing['64px']} 0`,
  padding: `0 ${theme.spacing['16px']} 0 ${theme.spacing['16px']}`,
  width: '100%',
}))

const TabContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: `${theme.spacing['24px']} 0 ${theme.spacing['24px']} 0`,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const ContentTitleContainer = styled(Subhead)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['4px'],
}))

const DomainContentContainer = styled.View<{ theme: ThemeType; isLastContent: boolean }>(
  ({ theme, isLastContent }) => ({
    marginBottom: isLastContent ? theme.spacing['0px'] : '28px',
  }),
)

const HRLine = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexGrow: 1,
  height: 1,
  backgroundColor: theme.colors.borderDefault,
}))

export const WellnessCheckInResultsMobileViewDomain: FunctionComponent<WellnessCheckInResultsDomainProps> = ({
  wellbeingResponse,
  tabKeys,
  domainScores,
  lowScoreDomains,
  onArticlePress,
  isPreview,
  onDomainPress,
  onLoad,
  onProviderTabCardPress,
  costMessagingProps,
}) => {
  const { colors } = useTheme() as ThemeType
  const [visibleDomains, setVisibleDomains] = useState<Set<string>>(new Set(tabKeys.length > 0 ? [tabKeys[0]] : []))

  const updateDomains = (domain: string) => {
    const tempVisibleDomains = new Set(visibleDomains)
    if (visibleDomains.has(domain)) {
      tempVisibleDomains.delete(domain)
    } else {
      tempVisibleDomains.add(domain)
    }

    setVisibleDomains(tempVisibleDomains)
  }

  const getDomainEssentialsContents = (results: WellbeingResponseResults) => {
    const contents = getWellnessCheckInEssentialsContents(results)
    return contents.map((content, index) => (
      <DomainContentContainer key={index} isLastContent={index === contents.length - 1}>
        <WellnessCheckInResultsDomainContent
          title={content.title}
          body={content.body}
          articleContents={content.articleContents}
          onArticlePress={onArticlePress}
          isLastContent={index === contents.length - 1}
          key={`WellnessCheckInDomain-${index}`}
        />
      </DomainContentContainer>
    ))
  }

  const getIsMounted = useIsMounted()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (getIsMounted() && !hasInitialized.current) {
      onLoad()
      hasInitialized.current = true
    }
  }, [getIsMounted, onLoad])

  return (
    <DomainContainer testID={tID(`WellnessCheckInResultsMobileViewDomain`)}>
      {tabKeys.length > 0 &&
        tabKeys.map((domain, index) => {
          const isDomainVisible = visibleDomains.has(domain)
          const isProviderTab = domain === TALK_WITH_PROVIDER_TAB
          const domainCardScore = domainScores[domain as WellnessCheckInDomain]

          return !isProviderTab &&
            isInvalidDomain(
              domain as WellnessCheckInDomain,
              wellbeingResponse[domain as WellnessCheckInDomain].response,
            ) ? (
            <></>
          ) : (
            <View key={index}>
              <TabContainer
                testID={tID(`WellnessCheckInResultsMobileViewDomain-${domain}`)}
                onPress={
                  isPreview
                    ? undefined
                    : () => {
                        onDomainPress && onDomainPress(domain as WellnessCheckInDomain)
                        updateDomains(domain)
                      }
                }
              >
                <View>
                  <ContentTitleContainer
                    text={
                      <FormattedMessage
                        {...(isProviderTab
                          ? PROVIDER_TAB_TITLE_MSG
                          : getDomainDisplayName(domain as WellnessCheckInDomain))}
                      />
                    }
                    size={SubheadSize.SMALL}
                  />
                  {isPreview && index > 0 ? (
                    <WellnessCheckInResultsDomainCardScorePreview />
                  ) : isProviderTab ? (
                    <Subhead
                      text={<FormattedMessage {...PROVIDER_TAB_SUBTITLE_MSG} />}
                      color={colors.textSecondary}
                      size={SubheadSize.XSMALL}
                    />
                  ) : (
                    domainCardScore && <WellnessCheckInResultsDomainCardScore score={domainCardScore} />
                  )}
                </View>
                <ChevronIcon
                  direction={isDomainVisible ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
                  fillColor={colors.iconDefault}
                  size={24}
                />
              </TabContainer>
              {isDomainVisible &&
                (isProviderTab ? (
                  <WellnessCheckInResultsProviderTabContent
                    lowScoreDomains={lowScoreDomains}
                    costMessagingProps={costMessagingProps}
                    onProviderTabCardPress={onProviderTabCardPress}
                  />
                ) : (
                  getDomainEssentialsContents(wellbeingResponse[domain as WellnessCheckInDomain].response)
                ))}
              {index != tabKeys.length - 1 && !isDomainVisible && <HRLine />}
            </View>
          )
        })}
    </DomainContainer>
  )
}
